<script setup>
import { ref, useSlots } from 'vue';
import { vOnClickOutside } from '@vueuse/components';

import Breadcrumb from 'dashboard/components-next/breadcrumb/Breadcrumb.vue';
import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  breadcrumbItems: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['back']);

const slots = useSlots();
const isSidebarOpen = ref(false);

const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value;
};

const closeMobileSidebar = () => {
  if (!isSidebarOpen.value) {
    return;
  }

  isSidebarOpen.value = false;
};
</script>

<template>
  <section
    class="flex h-full w-full justify-evenly overflow-hidden bg-auth-canvas text-auth-text dark:bg-auth-canvas-dark dark:text-auth-text-dark"
  >
    <div
      class="flex h-full min-w-0 flex-1 flex-col transition-all duration-300"
    >
      <header
        class="sticky top-0 z-10 border-b border-auth-border bg-auth-canvas/95 px-3 dark:border-auth-border-dark dark:bg-auth-canvas-dark/95 sm:px-5 lg:px-6"
      >
        <div class="mx-auto w-full max-w-5xl">
          <div
            class="flex w-full flex-col items-start justify-between gap-3 py-4 xs:flex-row xs:items-center sm:py-5"
          >
            <Breadcrumb :items="breadcrumbItems" @click="emit('back')" />
          </div>
        </div>
      </header>

      <main class="flex-1 overflow-y-auto px-3 sm:px-5 lg:px-6">
        <div class="mx-auto w-full max-w-5xl py-5 sm:py-6">
          <slot />
        </div>
      </main>
    </div>

    <div
      v-if="slots.sidebar"
      class="hidden w-full min-w-80 max-w-sm flex-col border-s border-auth-border bg-auth-soft text-auth-text dark:border-auth-border-dark dark:bg-auth-soft-dark dark:text-auth-text-dark lg:flex xl:max-w-md"
    >
      <div class="shrink-0">
        <slot name="sidebarHeader" />
      </div>
      <div class="flex-1 min-h-0 overflow-y-auto pb-6 pt-3">
        <slot name="sidebar" />
      </div>
    </div>

    <div
      v-if="slots.sidebar"
      class="fixed end-0 top-0 z-50 flex h-full justify-end transition-all duration-200 ease-in-out lg:hidden"
      :class="isSidebarOpen ? 'w-full' : 'w-16'"
    >
      <div
        v-on-click-outside="[
          closeMobileSidebar,
          { ignore: ['#details-sidebar-content'] },
        ]"
        class="relative order-1 top-28 flex h-fit w-fit items-start border border-auth-border bg-auth-soft p-1 transition-all duration-500 ease-in-out dark:border-auth-border-dark dark:bg-auth-soft-dark xs:top-24"
        :class="[
          isSidebarOpen
            ? 'justify-end rounded-s-full rounded-e-none'
            : 'me-6 justify-center rounded-full',
        ]"
      >
        <Button
          ghost
          slate
          sm
          class="!rounded-full !text-auth-muted hover:!bg-auth-border hover:!text-auth-text dark:!text-auth-muted-dark dark:hover:!bg-auth-border-dark dark:hover:!text-auth-text-dark rtl:rotate-180"
          :class="{
            '!bg-auth-border dark:!bg-auth-border-dark': isSidebarOpen,
          }"
          :icon="
            isSidebarOpen
              ? 'i-lucide-panel-right-close'
              : 'i-lucide-panel-right-open'
          "
          data-details-sidebar-toggle
          @click="toggleSidebar"
        />
      </div>

      <Transition
        enter-active-class="transition-transform duration-200 ease-in-out"
        leave-active-class="transition-transform duration-200 ease-in-out"
        enter-from-class="ltr:translate-x-full rtl:-translate-x-full"
        enter-to-class="ltr:translate-x-0 rtl:-translate-x-0"
        leave-from-class="ltr:translate-x-0 rtl:-translate-x-0"
        leave-to-class="ltr:translate-x-full rtl:-translate-x-full"
      >
        <div
          v-if="isSidebarOpen"
          id="details-sidebar-content"
          class="order-2 flex w-[85%] flex-col border-s border-auth-border bg-auth-soft text-auth-text shadow-lg dark:border-auth-border-dark dark:bg-auth-soft-dark dark:text-auth-text-dark sm:w-[50%]"
        >
          <div class="shrink-0">
            <slot name="sidebarHeader" />
          </div>
          <div class="flex-1 min-h-0 overflow-y-auto pb-6 pt-3">
            <slot name="sidebar" />
          </div>
        </div>
      </Transition>
    </div>
  </section>
</template>
